import { Icon, Style, Text, Stroke, Fill, Circle as CircleStyle } from 'ol/style.js';
import { Cluster, Vector as VectorSource } from 'ol/source.js';
import { Vector as VectorLayer } from 'ol/layer.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import LayerGroup from 'ol/layer/Group';
import TileLayer from 'ol/layer/Tile';

/**
 * UTILS
 */
import { getSvg } from '../../utils/misc';

// Función para crear una capa de cluster basada en los datos del JSON
function createClusterLayer(url, params, cluster, title) {

    const queryString = new URLSearchParams(params).toString();
    const fullUrl = `${url}&${queryString}`;

    const vectorSource = new VectorSource({
        url: fullUrl,
        format: new GeoJSON(),
    });

    const clusterSource = new Cluster({
        distance: parseInt(cluster.distance, 10),
        minDistance: parseInt(cluster.minDistance, 10),
        source: vectorSource,
        declutter: true,
    });

    const styleFunction = (feature) => getClusterStyle(feature, cluster.style);

    return new VectorLayer({
        source: clusterSource,
        style: styleFunction,
        title,
    });
}

// Función para convertir RGB o HEX a RGBA añadiendo la opacidad
function convertRgbToRgba(color, opacity) {
    if (!color) return color; // Return as-is if no color
    
    // Si es formato HEX (#72acdd o #abc)
    if (color.startsWith('#')) {
        let hex = color.slice(1);
        // Expandir formato corto (#abc -> #aabbcc)
        if (hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    
    // Si es formato RGB rgb(r, g, b)
    const rgbValues = color.match(/\d+/g);
    if (rgbValues && rgbValues.length >= 3) {
        return `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, ${opacity})`;
    }
    
    // Fallback - return original color if format not recognized
    return color;
}

// Función para definir el estilo de los clusters
// Agregar una propiedad a cada estilo para guardar la URL del SVG
const styleCache = new Map();
function getClusterStyle(feature, styleConfig, iconPath) {
    const size = feature.get('features').length;
    const clusterColor = styleConfig?.image?.fill?.color || '#3399CC';

    // Generamos una clave única para cachear por tamaño y color
    const cacheKey = `${size}-${clusterColor}`;

    // Si ya existe el estilo, lo devolvemos directamente
    if (styleCache.has(cacheKey)) {
        return styleCache.get(cacheKey);
    }

    // ⚙️ SVG para puntos individuales
    if (size === 1) {
        // Generamos el SVG solo una vez por feature
        if (!feature.get('svgUrl')) {
            const svgTemplate = getSvg(iconPath, clusterColor);
            const svgBlob = new Blob([svgTemplate], { type: 'image/svg+xml;charset=utf-8' });
            const svgUrl = URL.createObjectURL(svgBlob);
            feature.set('svgUrl', svgUrl);
        }

        const svgUrl = feature.get('svgUrl');

        const iconStyle = new Style({
            image: new Icon({
                src: svgUrl,
                scale: 1.2,
            }),
        });

        styleCache.set(cacheKey, iconStyle);
        return iconStyle;
    }

    // Estilo para clusters
    const clusterStyle = new Style({
        image: new CircleStyle({
            radius: styleConfig?.image?.radius || 10,
            fill: new Fill({
                color: convertRgbToRgba(clusterColor, styleConfig?.image?.fill?.opacity || 0.8),
            }),
            // stroke: new Stroke({
            //     color: styleConfig?.image?.stroke?.color || '#fff',
            //     width: styleConfig?.image?.stroke?.width || 2,
            // }),
        }),
        text: new Text({
            text: size.toString(),
            fill: new Fill({
                color: styleConfig?.text?.fill?.color || '#fff',
            }),
        }),
    });

    styleCache.set(cacheKey, clusterStyle);
    return clusterStyle;
}

/**
 * Función para obtener el zIndex de una capa en el mapa
 * @param {*} map 
 * @param {*} layerName 
 * @returns null si no se encuentra la capa, o el zIndex de la capa si se encuentra
 * @example
 * getLayerZIndex(map, '04_OC_OF_ALINEACION_EXTERIOR_line')
 */
function getLayerZIndex(map, layerName) {
    // Iterar sobre todas las capas del mapa
    const layers = map.getLayers().getArray();
    for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];

        // Verificar si la capa tiene el nombre que buscamos
        if (layer.get('name') === layerName) {
            // Obtener y devolver el zIndex de la capa
            // console.log(`Zindex de la capa ${layerName}: ${layer.getZIndex()}`)
            return layer.getZIndex();
        }
    }

    // Si no encuentra la capa, devolver un valor indicando que no fue encontrada
    return null;
}


const logMapLayers = (map) => {
    const printLayerInfo = (layer, indent = 0) => {
        const prefix = '  '.repeat(indent);
        const name = layer.get('name') || '[sin nombre]';
        const visible = layer.getVisible?.() ? '✅ visible' : '🚫 oculto';
        const zIndex = layer.getZIndex?.() ?? 'n/a';

        // Identificar el tipo de capa
        const type = layer instanceof LayerGroup ? 'LayerGroup'
            : layer instanceof VectorLayer ? 'VectorLayer'
                : layer instanceof TileLayer ? 'TileLayer'
                    : layer.constructor.name;

        let extra = `title ${title} | zIndex: ${zIndex}`;

        // Si es vectorial, mostrar número de features
        const source = layer.getSource?.();
        if (source?.getFeatures) {
            const featureCount = source.getFeatures().length;
            extra += ` | 🧬 features: ${featureCount}`;
        }

        // Si tiene URL (por ejemplo en capas Tile), mostrarla
        if (source?.getUrls) {
            const urls = source.getUrls();
            if (urls?.length) {
                extra += ` | 🌍 url: ${urls[0]}`;
            }
        }

        console.log(`${prefix}- ${type}: "${name}" (${visible}) → ${extra}`);

        // Si es un grupo, recorrer sus subcapas
        if (layer.getLayers) {
            const sublayers = layer.getLayers().getArray();
            sublayers.forEach(sub => printLayerInfo(sub, indent + 1));
        }
    };

    console.log('🗺️ Capas cargadas en el mapa:');
    const layers = map.getLayers().getArray();
    layers.forEach(layer => printLayerInfo(layer));
};



export { createClusterLayer, getClusterStyle, getLayerZIndex, logMapLayers }